/**
 * preferencesApi — generic per-user UI preferences.
 *
 * Backend: /user-preferences/{scope}/{key}
 * Returns BOTH default + user payloads; consumer (`usePreference` hook) merges
 * them. PATCH = deep-merge against user row. DELETE without sub_key resets the
 * whole user row; with sub_key clears just one nested entry.
 *
 * Tag tuple `(scope, key)` keyed cache so different (scope,key) pairs cache
 * independently. See `services/preferences/registry.py` for shape contracts.
 */
import { apiSlice } from './apiSlice'

export interface PreferenceResponse {
  scope: string
  key: string
  default: Record<string, unknown>
  user: Record<string, unknown>
}

export interface PreferencePatchArg {
  scope: string
  prefKey: string
  patch: Record<string, unknown>
}

export interface PreferenceResetArg {
  scope: string
  prefKey: string
  subKey?: string
}

export interface PreferenceSeedArg {
  scope: string
  prefKey: string
  subKey?: string
}

export interface PreferenceSeedResponse {
  scope: string
  key: string
  sub_key: string | null
  /** 'seed-new' | 'merge-subkey' | 'drift-update' | 'skip-identical' */
  action: 'seed-new' | 'merge-subkey' | 'drift-update' | 'skip-identical'
}

const tagFor = (scope: string, prefKey: string) =>
  ({ type: 'Preference' as const, id: `${scope}/${prefKey}` })

const preferencesApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getPreference: builder.query<PreferenceResponse, { scope: string; prefKey: string }>({
      query: ({ scope, prefKey }) => ({
        url: `/user-preferences/${encodeURIComponent(scope)}/${encodeURIComponent(prefKey)}`,
      }),
      providesTags: (_res, _err, { scope, prefKey }) => [tagFor(scope, prefKey)],
    }),

    patchPreference: builder.mutation<{ message: string }, PreferencePatchArg>({
      query: ({ scope, prefKey, patch }) => ({
        url: `/user-preferences/${encodeURIComponent(scope)}/${encodeURIComponent(prefKey)}`,
        method: 'PATCH',
        body: { patch },
      }),
      onQueryStarted: async ({ scope, prefKey, patch }, { dispatch, queryFulfilled }) => {
        const undo = dispatch(
          preferencesApi.util.updateQueryData(
            'getPreference',
            { scope, prefKey },
            (draft) => {
              draft.user = deepMerge(draft.user ?? {}, patch)
            },
          ),
        )
        try {
          await queryFulfilled
        } catch {
          undo.undo()
        }
      },
    }),

    seedPreferenceDefault: builder.mutation<PreferenceSeedResponse, PreferenceSeedArg>({
      query: ({ scope, prefKey, subKey }) => {
        const base = `/user-preferences/${encodeURIComponent(scope)}/${encodeURIComponent(prefKey)}/seed-default`
        const qs = subKey ? `?sub_key=${encodeURIComponent(subKey)}` : ''
        return { url: `${base}${qs}`, method: 'POST' }
      },
      // Invalidate cached getPreference so next read reflects the (possibly) new default.
      invalidatesTags: (_res, _err, { scope, prefKey }) =>
        _res && _res.action !== 'skip-identical' ? [tagFor(scope, prefKey)] : [],
    }),

    resetPreference: builder.mutation<{ message: string }, PreferenceResetArg>({
      query: ({ scope, prefKey, subKey }) => {
        const base = `/user-preferences/${encodeURIComponent(scope)}/${encodeURIComponent(prefKey)}`
        return {
          url: subKey ? `${base}/${encodeURIComponent(subKey)}` : base,
          method: 'DELETE',
        }
      },
      onQueryStarted: async ({ scope, prefKey, subKey }, { dispatch, queryFulfilled }) => {
        const undo = dispatch(
          preferencesApi.util.updateQueryData(
            'getPreference',
            { scope, prefKey },
            (draft) => {
              if (!subKey) {
                draft.user = {}
              } else if (draft.user && typeof draft.user === 'object') {
                delete (draft.user as Record<string, unknown>)[subKey]
              }
            },
          ),
        )
        try {
          await queryFulfilled
        } catch {
          undo.undo()
        }
      },
    }),
  }),
})

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v)
}

function deepMerge(base: Record<string, unknown>, patch: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = { ...base }
  for (const [k, v] of Object.entries(patch)) {
    if (isPlainObject(v) && isPlainObject(out[k])) {
      out[k] = deepMerge(out[k] as Record<string, unknown>, v)
    } else {
      out[k] = v
    }
  }
  return out
}

export const {
  useGetPreferenceQuery,
  usePatchPreferenceMutation,
  useResetPreferenceMutation,
  useSeedPreferenceDefaultMutation,
} = preferencesApi
